import React, { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Text } from "@react-three/drei";
import * as THREE from "three";
import { COLORS } from "../constants";
import { useDayFactor, makeColorLerper } from "../context";

function useSharedWindowMaterial() {
  const dayRef = useDayFactor();
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(COLORS.goldBright),
        emissive: new THREE.Color(COLORS.gold),
        emissiveIntensity: 0.55,
        metalness: 0.4,
        roughness: 0.4,
      }),
    []
  );
  const lerp = useMemo(() => makeColorLerper(), []);
  const colorRef = useMemo(() => new THREE.Color(), []);

  useFrame(() => {
    const f = dayRef.current;
    const night = 1 - f;
    mat.emissiveIntensity = 0.05 + night * 0.75;
    lerp(colorRef, COLORS.goldBright, "#cfd9e6", f);
    mat.color.copy(colorRef);
  });
  return mat;
}

function Building({ position, rotation, size = [18, 10, 8], label, windowMat }) {
  const [w, h, d] = size;
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh castShadow receiveShadow position={[0, h / 2, 0]}>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={COLORS.brick} roughness={0.95} />
      </mesh>
      <mesh receiveShadow position={[0, 0.6, 0]}>
        <boxGeometry args={[w + 0.4, 1.2, d + 0.4]} />
        <meshStandardMaterial color={COLORS.stoneLight} roughness={0.9} />
      </mesh>
      <mesh castShadow position={[0, h + 1.0, 0]}>
        <boxGeometry args={[w + 0.6, 2.0, d + 0.6]} />
        <meshStandardMaterial color={COLORS.brickRoof} roughness={0.7} metalness={0.2} />
      </mesh>
      <mesh castShadow position={[0, h + 2.3, d / 2 - 0.2]}>
        <coneGeometry args={[w * 0.45, 2.2, 3]} />
        <meshStandardMaterial color={COLORS.stoneLight} roughness={0.85} />
      </mesh>
      <mesh position={[0, 2.2, d / 2 + 0.05]}>
        <boxGeometry args={[3.2, 4.4, 0.2]} />
        <meshStandardMaterial color={COLORS.navyDeep} roughness={0.5} metalness={0.2} />
      </mesh>
      <mesh position={[0, 4.6, d / 2 + 0.06]}>
        <cylinderGeometry args={[1.6, 1.6, 0.2, 24, 1, false, 0, Math.PI]} />
        <meshStandardMaterial color={COLORS.stoneLight} roughness={0.85} />
      </mesh>
      {/* Windows — share material, NO shadow casting for perf */}
      {Array.from({ length: 2 }).map((_, row) =>
        Array.from({ length: 5 }).map((__, col) => {
          const skip = row === 0 && col === 2;
          if (skip) return null;
          const wx = -w / 2 + (col + 0.5) * (w / 5);
          const wy = 2.5 + row * 3.5;
          return (
            <mesh
              key={`w-${row}-${col}`}
              position={[wx, wy, d / 2 + 0.06]}
              material={windowMat}
            >
              <boxGeometry args={[1.2, 1.8, 0.1]} />
            </mesh>
          );
        })
      )}
      {[-1.9, 1.9].map((cx) => (
        <mesh key={cx} castShadow position={[cx, 2.2, d / 2 + 0.5]}>
          <cylinderGeometry args={[0.22, 0.22, 4.4, 12]} />
          <meshStandardMaterial color={COLORS.stoneLight} roughness={0.85} />
        </mesh>
      ))}
      <mesh position={[0, h + 4.3, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 3, 6]} />
        <meshStandardMaterial color={COLORS.stoneDark} />
      </mesh>
      {label && (
        <Text
          position={[0, h - 0.6, d / 2 + 0.12]}
          fontSize={0.55}
          color={COLORS.goldBright}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor={COLORS.navyDeep}
        >
          {label}
        </Text>
      )}
    </group>
  );
}

export default function Buildings() {
  const windowMat = useSharedWindowMaterial();
  return (
    <group>
      <Building
        position={[0, 0, -34]}
        rotation={Math.PI}
        size={[22, 11, 9]}
        label="BOARDROOM HALL"
        windowMat={windowMat}
      />
      <Building
        position={[0, 0, 34]}
        rotation={0}
        size={[22, 11, 9]}
        label="GREAT LIBRARY"
        windowMat={windowMat}
      />
      <Building
        position={[34, 0, 0]}
        rotation={-Math.PI / 2}
        size={[20, 10, 9]}
        label="LECTURE HALL"
        windowMat={windowMat}
      />
      <Building
        position={[-34, 0, 0]}
        rotation={Math.PI / 2}
        size={[20, 10, 9]}
        label="BELL TOWER"
        windowMat={windowMat}
      />
    </group>
  );
}
